import React from 'react';

interface TurmaValidationError {
  nome: string;
  cargaHorariaTotal: number;
  cargaHorariaAgenda: number;
  agendaNome: string;
}

interface ProfessorValidationError {
  nome: string;
  horariosLivres: number;
  aulasNecessarias: number;
}

interface CargaHorariaValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedAnyway?: () => void;
  turmasComErro: TurmaValidationError[];
  professoresComErro?: ProfessorValidationError[];
  cargaHorariaPeriodo: number;
}

const CargaHorariaValidationModal: React.FC<CargaHorariaValidationModalProps> = ({
  isOpen,
  onClose,
  onProceedAnyway,
  turmasComErro,
  professoresComErro = [],
  cargaHorariaPeriodo
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        tabIndex={-1} 
        style={{ zIndex: 1050 }}
        role="dialog"
        aria-labelledby="cargaHorariaValidationModalLabel"
        aria-hidden="false"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            {/* Header */}
            <div className="modal-header bg-primary text-white border-0">
              <div className="d-flex align-items-center">
                <div 
                  className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                  style={{ width: '50px', height: '50px' }}
                >
                  <i className="fas fa-exclamation text-white fa-lg"></i>
                </div>
                <div>
                  <h4 className="modal-title mb-1" id="cargaHorariaValidationModalLabel">
                    Inconsistência na Carga Horária
                  </h4>
                  <p className="mb-0 text-white-50" style={{ fontSize: '0.9rem' }}>
                    Algumas turmas possuem carga horária diferente do período
                  </p>
                </div>
              </div>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={onClose}
                aria-label="Fechar"
              ></button>
            </div>
            
            {/* Body */}
            <div className="modal-body p-4">
              <div className="row">
                {/* Informação do Período */}
                <div className="col-12 mb-4">
                  <div className="bg-light rounded-3 p-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-info-circle text-primary me-2"></i>
                      <h6 className="mb-0 text-primary fw-bold">Informação</h6>
                    </div>
                    <p className="mb-0 text-dark">
                      A validação agora é feita com base na <span className="fw-bold">agenda de cada turma</span>, considerando apenas os horários livres/disponíveis.
                    </p>
                    {onProceedAnyway && (
                      <p className="mb-0 mt-2 text-dark">
                        Isso é só um aviso — você ainda pode gerar a grade mesmo assim; o sistema vai tentar encaixar o máximo possível e indicar o que ficou pendente.
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Lista de Turmas com Erro */}
                <div className="col-12">
                  <div className="d-flex align-items-center mb-3">
                    <i className="fas fa-users text-danger me-2"></i>
                    <h6 className="mb-0 text-danger fw-bold">
                      {turmasComErro.length > 0 && professoresComErro.length > 0 ? 'Problemas Encontrados' : 
                       turmasComErro.length > 0 ? 'Turmas com Carga Horária Inconsistente' : 
                       'Professores com Horários Insuficientes'}
                    </h6>
                  </div>
                  
                  <div className="row g-3">
                    {/* Seção de Turmas com Erro */}
                    {turmasComErro.length > 0 && (
                      <div className="col-12 mb-3">
                        <h6 className="text-primary fw-bold mb-3">
                          <i className="fas fa-graduation-cap me-2"></i>
                          Turmas com Carga Horária Inconsistente
                        </h6>
                        <div className="row g-3">
                          {turmasComErro.map((turma, index) => {
                      const diferenca = turma.cargaHorariaTotal - turma.cargaHorariaAgenda;
                      const isExcesso = diferenca > 0;
                      
                      return (
                        <div key={index} className="col-12">
                          <div className="card border-0 bg-light">
                            <div className="card-body p-3">
                              <div className="row align-items-center">
                                <div className="col-md-8">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
                                        isExcesso ? 'bg-danger' : 'bg-warning'
                                      }`} 
                                      style={{ width: '40px', height: '40px' }}
                                    >
                                      <i className={`fas ${
                                        isExcesso ? 'fa-arrow-up' : 'fa-arrow-down'
                                      } text-white`}></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-1 fw-bold text-dark">{turma.nome}</h6>
                                      <p className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                                        <strong>Agenda:</strong> {turma.agendaNome}
                                      </p>
                                      <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                                        Disciplinas: <span className="fw-semibold">{turma.cargaHorariaTotal} aulas</span> | 
                                        Horários livres: <span className="fw-semibold">{turma.cargaHorariaAgenda} aulas</span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-4 text-md-end">
                                  <div className={`badge ${
                                    isExcesso ? 'bg-danger' : 'bg-warning'
                                  } fs-6 px-3 py-2`}>
                                    {isExcesso ? '+' : ''}{diferenca} aula{Math.abs(diferenca) !== 1 ? 's' : ''}
                                  </div>
                                  <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.8rem' }}>
                                    {isExcesso ? 'Excesso' : 'Faltam'} na agenda
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Seção de Professores com Erro */}
                    {professoresComErro.length > 0 && (
                      <div className="col-12">
                        <h6 className="text-warning fw-bold mb-3">
                          <i className="fas fa-chalkboard-teacher me-2"></i>
                          Professores com Horários Insuficientes
                        </h6>
                        <div className="row g-3">
                          {professoresComErro.map((professor, index) => {
                            const diferenca = professor.aulasNecessarias - professor.horariosLivres;
                            
                            return (
                              <div key={index} className="col-12">
                                <div className="card border-0 bg-light">
                                  <div className="card-body p-3">
                                    <div className="row align-items-center">
                                      <div className="col-md-8">
                                        <div className="d-flex align-items-center">
                                          <div 
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-warning" 
                                            style={{ width: '40px', height: '40px' }}
                                          >
                                            <i className="fas fa-exclamation text-white"></i>
                                          </div>
                                          <div>
                                            <h6 className="mb-1 fw-bold text-dark">{professor.nome}</h6>
                                            <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                                              Aulas necessárias: <span className="fw-semibold">{professor.aulasNecessarias}</span> | 
                                              Horários livres: <span className="fw-semibold">{professor.horariosLivres}</span>
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-4 text-md-end">
                                        <div className="badge bg-warning fs-6 px-3 py-2">
                                          -{diferenca} horário{diferenca !== 1 ? 's' : ''}
                                        </div>
                                        <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.8rem' }}>
                                          Faltam na agenda
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Instruções */}
              <div className="mt-4 p-3 bg-info bg-opacity-10 rounded-3 border border-info border-opacity-25">
                <div className="d-flex align-items-start">
                  <i className="fas fa-lightbulb text-info me-3 mt-1"></i>
                  <div className="text-start">
                    <h6 className="text-info fw-bold mb-2">Como resolver:</h6>
                    <ul className="mb-0 text-dark text-start" style={{ fontSize: '0.9rem' }}>
                      {turmasComErro.length > 0 && (
                        <>
                          <li className="mb-1">Para <strong>turmas</strong>: acesse o menu <strong>Turmas e Disciplinas</strong> para ajustar as cargas horárias</li>
                          <li className="mb-1">Para turmas com <strong>falta de disciplinas</strong>: adicione mais disciplinas ou libere mais horários na agenda</li>
                          <li className="mb-1">Para turmas com <strong>excesso de disciplinas</strong>: remova disciplinas ou bloqueie horários na agenda</li>
                        </>
                      )}
                      {professoresComErro.length > 0 && (
                        <>
                          <li className="mb-1">Para <strong>professores</strong>: acesse o menu <strong>Professores</strong> para gerenciar as agendas</li>
                          <li className="mb-1">Libere mais horários na agenda do professor ou reduza a carga horária das disciplinas</li>
                        </>
                      )}
                      <li>Certifique-se de que todos os professores tenham horários suficientes em suas agendas para suas disciplinas.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="modal-footer border-0 bg-light">
              <div className="d-flex gap-2 w-100 justify-content-end">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary px-4"
                  onClick={onClose}
                  style={{ borderRadius: '8px', fontWeight: '600' }}
                >
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={() => {
                    onClose();
                    // Navegar para disciplinas (implementar se necessário)
                    window.location.href = '/disciplinas';
                  }}
                  style={{ borderRadius: '8px', fontWeight: '600', boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)' }}
                >
                  <i className="fas fa-cog me-2"></i>
                  Gerenciar Turmas e Disciplinas
                </button>
                {onProceedAnyway && (
                  <button
                    type="button"
                    className="btn btn-warning px-4"
                    onClick={onProceedAnyway}
                    style={{ borderRadius: '8px', fontWeight: '600', boxShadow: '0 2px 4px rgba(255, 193, 7, 0.3)' }}
                  >
                    <i className="fas fa-magic me-2"></i>
                    Gerar Mesmo Assim
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CargaHorariaValidationModal;